<template>
  <div class="mk-start">
    <img class="mk-start__image" src="../assets/logo.svg" />
    <span class="mk-start__title">
      {{$t('start.title')}}
    </span>
    <div class="mk-start__privacy" :class="privacyError?'mk-start__privacy--error':''">
      <input type="checkbox" id="privacy" name="privacy" v-model="privacyPolicyAccepted">
      <label for="privacy">
        {{$t('start.accept')}}
        <a href="https://mk-solutions.com/en/privacy-policy/">
          {{$t('start.privacy')}}
        </a>
      </label>
    </div>
    <img class="mk-start__spinner" src="../assets/spinner.svg" v-if="!loadedData"/>
    <button
      v-else
      class="mk-start__continue"
      @click="submit"
    >
      Start
    </button>
    <LoadSavedPopup
      v-if="loadedData && loadedData.dimensions"
      :closeCallback="() => {loadedData.dimensions = null;}"
      :loadCallback="loadLoadedData"
    />
  </div>
</template>

<script>
/**
 * Start
 * @author David <david.prenninger@gmx.de>
 *
 * This component is part of the main configurator Sections.
 * On startup this component loads all models and resources,
 * checks the user IP and resolves a country code from it.
 *
 * @dependency node-iplocate Is used to retrieve correct user IP location.
 */
import { HOST_SITE } from '@/constants';
import { getProductData } from '@/helpers/api';
import ObjectLoader from '../helpers/visualization/ObjectLoader';
import LoadSavedPopup from '../components/LoadSavedPopup.vue';
import { deserialize, resetURL, getUserDetails } from '../helpers/url';
import { checkIfPointIsClampApproved, checkForStandApproval } from '../helpers/dimensions';

export default {
  components: {
    LoadSavedPopup,
  },
  data() {
    return {
      privacyPolicyAccepted: false,
      privacyError: false,
      loadedData: null,
    };
  },
  created() {
    // Load translations from wordpress
    // LOAD GERMAN
    fetch(`${HOST_SITE}/wp-content/plugins/mk-configurator-plugin-vue/res/locales/de.json`)
      .then((response) => response.json())
      .then((data) => {
        this.$i18n.setLocaleMessage('de', data);
      });
    // LOAD DANISH
    fetch(`${HOST_SITE}/wp-content/plugins/mk-configurator-plugin-vue/res/locales/dk.json`)
      .then((response) => response.json())
      .then((data) => {
        this.$i18n.setLocaleMessage('dk', data);
      });
    // LOAD English
    fetch(`${HOST_SITE}/wp-content/plugins/mk-configurator-plugin-vue/res/locales/en.json`)
      .then((response) => response.json())
      .then((data) => {
        this.$i18n.setLocaleMessage('en', data);
      });

    getProductData(this).then(() => {
      // Retrieve Product list from Woocommerce
      ObjectLoader.loadFromProductList(this.$store);
      this.loadedData = deserialize(this);
      getUserDetails(this);
    });
  },
  methods: {
    submit() {
      if (this.privacyPolicyAccepted) {
        this.$store.dispatch('nextStep');
        resetURL(this);
        return;
      }
      this.privacyError = true;
    },
    loadLoadedData() {
      this.$store.dispatch('setLoading', true);
      if (this.loadedData.dimensions && this.loadedData.offset && !this.loadedData.objectSections) {
        this.$store.dispatch('loadDimensions', this.loadedData.dimensions);
        this.$store.dispatch('loadOffset', this.loadedData.offset);
        this.$store.dispatch('setFinish', this.loadedData.finished);
        this.$store.dispatch('nextStep');
        this.$store.dispatch('nextStep');
      } else if (
        this.loadedData.dimensions
        && this.loadedData.offset
        && this.loadedData.objectSections
      ) {
        this.$store.dispatch('loadDimensions', this.loadedData.dimensions);
        this.$store.dispatch('loadOffset', this.loadedData.offset);
        this.$store.dispatch('loadSectionObjects', this.loadedData.objectSections);
        this.$store.dispatch('setFinish', this.loadedData.finished);
        checkIfPointIsClampApproved(
          this.$store.getters.objectSections,
          this.$store.getters.dimensions,
          this.$store.getters.offset,
          this,
        );
        checkForStandApproval(
          this.$store.getters.objectSections,
          this.$store.getters.dimensions,
          this.$store.getters.offset,
          this,
        );
        this.$store.dispatch('nextStep');
        this.$store.dispatch('nextStep');
        this.$store.dispatch('nextStep');
      }
      this.$store.dispatch('setLoading', false);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/defaults.scss";

.mk-start {
  position: absolute;
  z-index: 5;
  height: 100%;
  width: 100%;
  background-color: $c_accent;
  color:$c_white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;

  &__image {
    height: 100px;
  }

  &__title {
    font-size: 24px;
    font-weight: 500;
  }

  &__privacy {
    padding: 20px 10px;
    transition: all 0.5s ease-in;

    a {
      color: $c_white;

      &:not(.btn-text-skin):hover {
        color: $c_white
      }
    }

    &--error {
      color: $c_accent;
      background-color: $c_white;
      border-radius: $br_md;
      box-shadow: $bs_md;

      a {
        color: $c_accent;

        &:not(.btn-text-skin):hover {
          color: $c_accent;
        }
      }
    }
  }

  &__continue {
    background-color: $c_white;
    color: $c_accent;
    padding: 10px 20px;
    border-radius: $br_md;
    box-shadow: $bs_md;
    border: none;
    width: fit-content;

    &:not(.toggle) {
      background-color: $c_white;
    }
  }

  &__spinner {
    animation: spin 2s linear infinite;
    height: 40px;
  }

  @keyframes spin { 100% { -webkit-transform: rotate(360deg); transform:rotate(360deg); } }
}
</style>
